import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "divide-y divide-gray-200 space-y-4 px-4 py-4" }
const _hoisted_2 = { class: "flex justify-between items-center space-x-3" }
const _hoisted_3 = { class: "flex items-center space-x-3" }
const _hoisted_4 = { class: "font-bold text-lg" }
const _hoisted_5 = { class: "text-sm text-gray-500" }
const _hoisted_6 = { class: "flex shrink-0 items-center gap-x-3" }
const _hoisted_7 = { class: "inline-flex rounded-lg border border-gray-200 bg-white p-0.5" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 1,
  class: "px-4 pb-4 space-y-3"
}
const _hoisted_10 = { class: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 rounded-lg bg-gray-50 p-2" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "min-w-0" }
const _hoisted_13 = { class: "flex items-center justify-between gap-x-2 rounded-t-lg border-b border-primary-500 bg-primary-300/30 px-4 py-2.5" }
const _hoisted_14 = { class: "flex items-center gap-x-2" }
const _hoisted_15 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = ["onClick"]

import { ref, computed, h, inject, Ref } from 'vue';
import { ColumnDef, SortingState } from '@tanstack/vue-table';
import { Plus, History } from 'lucide-vue-next';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import { format } from 'date-fns';
import MainViewCard from '@/components/common/MainViewCard.vue';
import Icon from '@/components/icons/Icon.vue';
import { IconSource, ProductionGroup, ProductionGroupBarnResponseEntry } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';
import Badge from '@/components/ui/badge/Badge.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import DataTable from '@/components/ui/data-table/DataTable.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { DataTableFacetFilter, type FacetSelection } from '@/components/ui/data-table';
import { queryKeys } from '@/plugins/store/actions/queries/production-groups';
import { durationDays, formatDuration } from './useProductionGroupBackfill';
import GroupFavoriteToggle from './GroupFavoriteToggle.vue';
import { useSectionById } from './useSectionById';

// Caps each Barn's own group table at this height, scrolling nested rows past a sticky header
// rather than growing the page - same treatment the records tables (Pigs/Slaughters) use for large
// row counts.
const MAX_BODY_HEIGHT = 'max-h-[60vh]';


export default /*@__PURE__*/_defineComponent({
  __name: 'BarnSection',
  props: {
    barn: {},
    searchActive: { type: Boolean },
    manualViewMode: {}
  },
  emits: ["create", "edit", "backfill", "update:manualViewMode"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

// Groups overview is a management surface - name/dates/favorite/creation type, the facts a farmer
// edits or checks a group by - not a KPI readout. Growth index and Animal group are read-only
// figures that belong on Active groups' own per-Section view instead, so neither column nor the
// growth-interval selector that would drive it appears here.
//
// Explicit per-column widths, applied to both header and cell via `meta.headerClass`/`cellClass` and
// paired with `table-fixed` on the <table> (see template) - each Section renders its own independent
// DataTable instance, so without a fixed layout a long value in one Section's Name column (e.g. a
// backfilled group's auto-generated name) widens only that table, throwing its columns out of
// alignment with every other expanded Section's table above/below it. Name is the one column left
// unconstrained, so it absorbs whatever width table-fixed leaves over.
const COLUMN_WIDTH = {
    select: 'w-12',
    location: 'w-32',
    start: 'w-28',
    end: 'w-28',
    duration: 'w-24',
    creationType: 'w-36',
    favorite: 'w-16',
};

// Its own computed, not an inline entry in `columns` below, so `sectionColumns` can filter it out by
// reference identity - the By-section view repeats this same column set minus Location (every table
// is already scoped to one Section there), and a plain object identity check needs no cast into
// `ColumnDef`'s accessor/display/group union the way reading `.accessorKey` off it would.
const locationColumn = computed<ColumnDef<ProductionGroup & { _locationName: string }>>(() => ({
    header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.location'),
    accessorKey: '_locationName',
    enableSorting: true,
    meta: { headerClass: COLUMN_WIDTH.location, cellClass: COLUMN_WIDTH.location },
}));

const columns = computed<ColumnDef<ProductionGroup & { _locationName: string }>[]>(() => [
    {
        id: '_select',
        header: ({ table }) =>
            h(Checkbox, {
                checked: table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate'),
                'onUpdate:checked': (value) => table.toggleAllPageRowsSelected(!!value),
                ariaLabel: i18n.global.t('views.monitoring.groupOverview.fields.selectAll.label'),
                class: 'mt-1',
            }),
        cell: ({ row }) =>
            h(Checkbox, {
                checked: row.getIsSelected(),
                'onUpdate:checked': (value) => row.toggleSelected(!!value),
                ariaLabel: i18n.global.t('views.monitoring.groupOverview.fields.selectGroup.label'),
                class: 'mt-1',
                // Row itself opens Edit on click; the checkbox needs its own click kept local.
                onClick: (event: Event) => event.stopPropagation(),
            }),
        meta: { headerClass: COLUMN_WIDTH.select, cellClass: COLUMN_WIDTH.select },
    },
    locationColumn.value,
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.name'),
        accessorKey: 'name',
        enableSorting: true,
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.startDate'),
        accessorKey: 'start',
        enableSorting: true,
        meta: { headerClass: COLUMN_WIDTH.start, cellClass: COLUMN_WIDTH.start },
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.endDate'),
        accessorKey: 'end',
        enableSorting: true,
        meta: { headerClass: COLUMN_WIDTH.end, cellClass: COLUMN_WIDTH.end },
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.duration'),
        accessorKey: '_duration',
        meta: { headerClass: COLUMN_WIDTH.duration, cellClass: COLUMN_WIDTH.duration },
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.creationType'),
        accessorKey: 'is_auto_generated',
        enableSorting: true,
        meta: {
            align: 'center',
            headerClass: COLUMN_WIDTH.creationType,
            cellClass: COLUMN_WIDTH.creationType,
        },
    },
    {
        header: '',
        accessorKey: 'is_favorite',
        meta: { headerClass: COLUMN_WIDTH.favorite, cellClass: COLUMN_WIDTH.favorite },
    },
]);
const sorting = ref<SortingState>([
    {
        id: 'start',
        desc: true,
    },
]);

// Each per-Section table (see template, by-Section mode) gets its own independent sorting/page/
// pageSize, created once per Section id and cached here - NOT this component's single top-level
// `sorting`/`page`/`pageSize` above, which stays reserved for the flat ('all') table only. Several
// Section tables can be expanded at once (e.g. a search-driven mass expand), each mounting/updating
// its own DataTable in the same reactive flush; one shared ref read and written by all of them at
// once overflows Vue's recursive-update guard, so sort/page state must stay isolated per Section.
// Row selection stays intentionally shared (see `rowSelection` below, and the cross-Section "Manage
// selected" behaviour it drives) - only sort/page state needs isolating.
const sectionTableState = new Map<number, { sorting: Ref<SortingState>; page: Ref<number>; pageSize: Ref<number> }>();

function getSectionTableState(sectionId: number) {
    let state = sectionTableState.get(sectionId);

    if (!state) {
        state = {
            sorting: ref<SortingState>([{ id: 'start', desc: true }]),
            page: ref(1),
            pageSize: ref(9999),
        };
        sectionTableState.set(sectionId, state);
    }

    return state;
}

const sectionById = useSectionById();

function getSection(locationId: number) {
    return sectionById.value.get(locationId);
}

const queryClient = useQueryClient();
const toast = useToast();

// In flight by group id, so a second click on the same star while its request is still out doesn't
// fire a second overlapping edit (two in-flight toggles racing would leave the row showing whichever
// response lands last, not the farmer's actual last click).
const pendingFavoriteIds = ref<Set<number>>(new Set());

async function toggleFavorite(group: ProductionGroup) {
    if (pendingFavoriteIds.value.has(group.id)) {
        return;
    }

    pendingFavoriteIds.value = new Set(pendingFavoriteIds.value).add(group.id);

    try {
        // `group.start`/`group.end` are typed as `string` but arrive as already-parsed `Date`
        // objects (Index.vue's own `data` computed runs them through `parseISO` before handing
        // groups down to this component - see groupDuration's own `as unknown as Date` cast below).
        // Sent raw, a bare `JSON.stringify` turns them into full UTC timestamps, which the backend's
        // `DateOnly` binder rejects. Re-format to the plain yyyy-MM-dd it expects, same as the Manage
        // Group modal does for its own start/end fields.
        await store.dispatch(ActionType.EditProductionGroups, {
            options: { skipHandleError: true },
            form: {
                production_groups: [
                    {
                        id: group.id,
                        name: group.name,
                        note: group.note,
                        start: format(group.start as unknown as Date, 'yyyy-MM-dd'),
                        end: group.end ? format(group.end as unknown as Date, 'yyyy-MM-dd') : null,
                        is_favorite: !group.is_favorite,
                    },
                ],
            },
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.getProductionGroupsRoot });
    } catch {
        toast.error(i18n.global.t('views.monitoring.groupOverview.tables.barnTable.errors.couldNotUpdateFavorite'));
    } finally {
        const next = new Set(pendingFavoriteIds.value);
        next.delete(group.id);
        pendingFavoriteIds.value = next;
    }
}

// Days the group spans (inclusive of both ends) with the whole weeks they contain, formatted the
// same way the backfill wizard's own duration column does (useProductionGroupBackfill.ts) - calls
// its own durationDays rather than reimplementing the arithmetic, so the two can't disagree. Elapsed
// milliseconds between two real Date instants is not a reliable day count across a daylight-saving
// transition (a 23- or 25-hour local day still divides to 0 or still-incomplete-1 whole days); going
// through plain "yyyy-MM-dd" strings first, as durationDays itself takes, counts calendar dates
// instead. A still-open group has no end yet, so it's measured through today - same "start to today"
// rule the backfill wizard itself uses for an open-ended suggestion's own duration.
function groupDuration(start: Date | null, end: Date | null): string {
    if (!start) {
        return '-';
    }

    const effectiveEnd = end ?? new Date();
    return formatDuration(durationDays(format(start, 'yyyy-MM-dd'), format(effectiveEnd, 'yyyy-MM-dd')));
}

const barnSections = computed(() => store.state.sections?.filter((s) => s.barn_id === props.barn.barn_id) ?? []);

const columnFilters = ref<FacetSelection>({});
const facets = computed(() => ({
    _locationName: {
        title: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.location'),
        options: barnSections.value.map((section) => ({ label: section.name, value: section.id })),
    },
}));

function setLocationFilter(value: (string | number)[]) {
    const next = { ...columnFilters.value };

    if (value.length) {
        next._locationName = value;
    } else {
        delete next._locationName;
    }

    columnFilters.value = next;
}

const items = computed(() => {
    const selectedLocationIds = columnFilters.value._locationName ?? [];

    return props.barn.production_groups
        .filter((sp) => selectedLocationIds.length === 0 || selectedLocationIds.includes(sp.location_id))
        .map((sp) => ({
            ...sp,
            _locationName: getSection(sp.location_id)?.name || '',
            _duration: groupDuration(sp.start as unknown as Date | null, sp.end as unknown as Date | null),
        }));
});

// By-Section is the default until the farmer picks a mode by hand - once they do, it sticks.
const viewMode = computed<'all' | 'bySection'>(() => props.manualViewMode ?? 'bySection');

// Same natural "Section 2" < "Section 10" numeric-aware name ordering Active groups uses
// (sortSections.ts `byName`) - its live-state tiers (age/attention) don't apply here since a tile
// just states a count for the selected range, not a current status, but the name ordering itself
// should read the same across both pages.
function compareSectionNames(a: { name: string }, b: { name: string }): number {
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

const orderedBarnSections = computed(() => [...barnSections.value].sort(compareSectionNames));

// The Location facet-filter also narrows which Section tiles render, same selection/row-set the flat
// table already filters on - no separate filter UI for the tile grid.
const visibleSections = computed(() => {
    const selectedLocationIds = columnFilters.value._locationName ?? [];
    return selectedLocationIds.length === 0 ? orderedBarnSections.value : orderedBarnSections.value.filter((section) => selectedLocationIds.includes(section.id));
});

const itemsBySectionId = computed(() => {
    const map = new Map<number, typeof items.value>();

    for (const item of items.value) {
        const existing = map.get(item.location_id);
        if (existing) {
            existing.push(item);
        } else {
            map.set(item.location_id, [item]);
        }
    }

    return map;
});

function sectionItems(sectionId: number) {
    return itemsBySectionId.value.get(sectionId) ?? [];
}

// Location is dropped from the per-Section table - redundant once you're already looking at one.
const sectionColumns = computed(() => columns.value.filter((column) => column !== locationColumn.value));

const manuallyExpandedSectionIds = ref<Set<number>>(new Set());

function toggleSectionExpanded(sectionId: number) {
    const next = new Set(manuallyExpandedSectionIds.value);
    if (next.has(sectionId)) {
        next.delete(sectionId);
    } else {
        next.add(sectionId);
    }
    manuallyExpandedSectionIds.value = next;
}

// While a search is active, every Section with a matching row auto-expands on top of whatever the
// farmer has opened by hand, so a result can never stay hidden behind a collapsed tile.
const expandedSectionIds = computed(() => {
    if (!props.searchActive) {
        return manuallyExpandedSectionIds.value;
    }

    const expanded = new Set(manuallyExpandedSectionIds.value);
    for (const section of visibleSections.value) {
        if (sectionItems(section.id).length > 0) {
            expanded.add(section.id);
        }
    }
    return expanded;
});

const page = ref(1);
const pageSize = ref(9999);
const pageSizeOptions = ref([10, 25, 50, 100]);

const { selections, updateSelections } = inject('barnSelections') as {
    selections: Ref<Record<number, Record<number, boolean>>>;
    // eslint-disable-next-line
    updateSelections: (barnId: number, selections: Record<number, boolean>) => void
};

const rowSelection = computed({
    get() {
        return selections.value[props.barn.barn_id] || {};
    },
    set(value) {
        updateSelections(props.barn.barn_id, value);
    },
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(MainViewCard, { class: "max-w-full overflow-hidden" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", null, [
              _createVNode(Icon, {
                class: "h-6",
                src: _unref(IconSource).Location
              }, null, 8, ["src"])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.barn.barn_name), 1),
              _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.barn.production_groups?.length || 0) + " " + _toDisplayString(_ctx.barn.production_groups?.length === 1 ? _ctx.$t('views.monitoring.groupOverview.texts.group') : _ctx.$t('views.monitoring.groupOverview.texts.groups')), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            (viewMode.value === 'bySection')
              ? (_openBlock(), _createBlock(_unref(DataTableFacetFilter), {
                  key: 0,
                  title: facets.value._locationName.title,
                  options: facets.value._locationName.options,
                  "model-value": columnFilters.value._locationName ?? [],
                  "onUpdate:modelValue": setLocationFilter
                }, null, 8, ["title", "options", "model-value"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["rounded-md px-3 py-1.5 text-sm font-medium transition-colors", viewMode.value === 'bySection' ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100']),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('update:manualViewMode', 'bySection')))
              }, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.fields.viewMode.bySection')), 3),
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["rounded-md px-3 py-1.5 text-sm font-medium transition-colors", viewMode.value === 'all' ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100']),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('update:manualViewMode', 'all')))
              }, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.fields.viewMode.allGroups')), 3)
            ]),
            _createElementVNode("button", {
              class: "flex items-center rounded-lg gap-x-1 text-sky-500 hover:bg-sky-400/10 transition-colors px-3.5 py-2",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('backfill', _ctx.barn.barn_id)))
            }, [
              _createVNode(_unref(History), {
                class: "size-5",
                "aria-hidden": "true"
              }),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.groupOverview.buttons.backfillHistory')), 1)
            ]),
            _createElementVNode("button", {
              class: "flex items-center rounded-lg gap-x-1 text-sky-500 hover:bg-sky-400/10 transition-colors px-3.5 py-2",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('create', _ctx.barn.barn_id)))
            }, [
              _createVNode(_unref(Plus), {
                class: "size-5",
                "aria-hidden": "true"
              }),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.groupOverview.buttons.createGroup')), 1)
            ])
          ])
        ])
      ]),
      (viewMode.value === 'all')
        ? (_openBlock(), _createBlock(DataTable, {
            key: 0,
            sorting: sorting.value,
            "onUpdate:sorting": _cache[4] || (_cache[4] = ($event: any) => ((sorting).value = $event)),
            "row-selection": rowSelection.value,
            "onUpdate:rowSelection": [
              _cache[5] || (_cache[5] = ($event: any) => ((rowSelection).value = $event)),
              _cache[6] || (_cache[6] = ($event: any) => (_unref(updateSelections)(_ctx.barn.barn_id, $event)))
            ],
            data: items.value,
            columns: columns.value,
            page: page.value,
            "page-size": pageSize.value,
            "page-size-options": pageSizeOptions.value,
            "enable-pagination": false,
            facets: facets.value,
            "column-filters": columnFilters.value,
            "max-body-height": MAX_BODY_HEIGHT,
            "table-class": "table-fixed",
            "row-clickable": "",
            "onUpdate:columnFilters": _cache[7] || (_cache[7] = ($event: any) => (columnFilters.value = $event)),
            onRowClick: _cache[8] || (_cache[8] = ($event: any) => (emit('edit', $event)))
          }, {
            "item[name]": _withCtx(({ item }) => [
              _createElementVNode("button", {
                type: "button",
                class: "rounded-sm text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700",
                onClick: _withModifiers(($event: any) => (emit('edit', item)), ["stop"])
              }, _toDisplayString(item.name), 9, _hoisted_8)
            ]),
            "item[start]": _withCtx(({ item }) => [
              _createTextVNode(_toDisplayString(item.start ? _ctx.$d(item.start as any, 'short') : '-'), 1)
            ]),
            "item[end]": _withCtx(({ item }) => [
              (!item.end)
                ? (_openBlock(), _createBlock(Badge, {
                    key: 0,
                    variant: "outline",
                    class: "border-transparent bg-green-100 text-green-700"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.tables.barnTable.ongoing')), 1)
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(_ctx.$d(item.end as any, 'short')), 1)
                  ], 64))
            ]),
            "item[is_auto_generated]": _withCtx(({ item }) => [
              _createTextVNode(_toDisplayString(item.is_auto_generated ? _ctx.$t('views.monitoring.groupOverview.creationTypes.autoGenerated') : _ctx.$t('views.monitoring.groupOverview.creationTypes.manuallyGenerated')), 1)
            ]),
            "item[is_favorite]": _withCtx(({ item }) => [
              _createVNode(GroupFavoriteToggle, {
                group: item,
                pending: pendingFavoriteIds.value.has(item.id),
                onToggle: toggleFavorite
              }, null, 8, ["group", "pending"])
            ]),
            _: 1
          }, 8, ["sorting", "row-selection", "data", "columns", "page", "page-size", "page-size-options", "facets", "column-filters"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleSections.value, (section) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: section.id,
                  type: "button",
                  class: _normalizeClass(["flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors", expandedSectionIds.value.has(section.id) ? 'border-primary-700 bg-primary-700 shadow-sm' : 'border-gray-200 bg-white hover:border-primary-500 hover:bg-primary-300/30']),
                  onClick: ($event: any) => (toggleSectionExpanded(section.id))
                }, [
                  _createElementVNode("span", _hoisted_12, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["block truncate font-medium", expandedSectionIds.value.has(section.id) ? 'text-white' : 'text-gray-900'])
                    }, _toDisplayString(section.name), 3),
                    _createElementVNode("span", {
                      class: _normalizeClass(["block text-sm", expandedSectionIds.value.has(section.id) ? 'text-white/80' : 'text-gray-500'])
                    }, _toDisplayString(sectionItems(section.id).length) + " " + _toDisplayString(sectionItems(section.id).length === 1 ? _ctx.$t('views.monitoring.groupOverview.texts.group') : _ctx.$t('views.monitoring.groupOverview.texts.groups')), 3)
                  ])
                ], 10, _hoisted_11))
              }), 128))
            ]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleSections.value, (section) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: `table-${section.id}`
              }, [
                (expandedSectionIds.value.has(section.id))
                  ? (_openBlock(), _createBlock(MainViewCard, {
                      key: 0,
                      class: "border-2 border-primary-500 shadow-md"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("span", _hoisted_14, [
                            _cache[12] || (_cache[12] = _createElementVNode("span", {
                              class: "size-2 shrink-0 rounded-full bg-primary-700",
                              "aria-hidden": "true"
                            }, null, -1)),
                            _createElementVNode("span", _hoisted_15, _toDisplayString(section.name), 1)
                          ]),
                          _createElementVNode("button", {
                            type: "button",
                            class: "flex items-center gap-x-1 rounded-md px-2 py-1 text-sm text-sky-600 transition-colors hover:bg-sky-400/10",
                            onClick: ($event: any) => (emit('backfill', _ctx.barn.barn_id, section.id))
                          }, [
                            _createVNode(_unref(History), {
                              class: "size-4",
                              "aria-hidden": "true"
                            }),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.groupOverview.buttons.backfillHistory')), 1)
                          ], 8, _hoisted_16)
                        ]),
                        _createVNode(DataTable, {
                          sorting: getSectionTableState(section.id).sorting.value,
                          "onUpdate:sorting": ($event: any) => ((getSectionTableState(section.id).sorting.value) = $event),
                          "row-selection": rowSelection.value,
                          "onUpdate:rowSelection": [
                            _cache[9] || (_cache[9] = ($event: any) => ((rowSelection).value = $event)),
                            _cache[10] || (_cache[10] = ($event: any) => (_unref(updateSelections)(_ctx.barn.barn_id, $event)))
                          ],
                          data: sectionItems(section.id),
                          columns: sectionColumns.value,
                          page: getSectionTableState(section.id).page.value,
                          "page-size": getSectionTableState(section.id).pageSize.value,
                          "page-size-options": pageSizeOptions.value,
                          "enable-pagination": false,
                          "max-body-height": MAX_BODY_HEIGHT,
                          "table-class": "table-fixed",
                          "row-clickable": "",
                          "preserve-foreign-selection": "",
                          onRowClick: _cache[11] || (_cache[11] = ($event: any) => (emit('edit', $event)))
                        }, {
                          "item[name]": _withCtx(({ item }) => [
                            _createElementVNode("button", {
                              type: "button",
                              class: "rounded-sm text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700",
                              onClick: _withModifiers(($event: any) => (emit('edit', item)), ["stop"])
                            }, _toDisplayString(item.name), 9, _hoisted_17)
                          ]),
                          "item[start]": _withCtx(({ item }) => [
                            _createTextVNode(_toDisplayString(item.start ? _ctx.$d(item.start as any, 'short') : '-'), 1)
                          ]),
                          "item[end]": _withCtx(({ item }) => [
                            (!item.end)
                              ? (_openBlock(), _createBlock(Badge, {
                                  key: 0,
                                  variant: "outline",
                                  class: "border-transparent bg-green-100 text-green-700"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.tables.barnTable.ongoing')), 1)
                                  ]),
                                  _: 1
                                }))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(_toDisplayString(_ctx.$d(item.end as any, 'short')), 1)
                                ], 64))
                          ]),
                          "item[is_auto_generated]": _withCtx(({ item }) => [
                            _createTextVNode(_toDisplayString(item.is_auto_generated ? _ctx.$t('views.monitoring.groupOverview.creationTypes.autoGenerated') : _ctx.$t('views.monitoring.groupOverview.creationTypes.manuallyGenerated')), 1)
                          ]),
                          "item[is_favorite]": _withCtx(({ item }) => [
                            _createVNode(GroupFavoriteToggle, {
                              group: item,
                              pending: pendingFavoriteIds.value.has(item.id),
                              onToggle: toggleFavorite
                            }, null, 8, ["group", "pending"])
                          ]),
                          _: 2
                        }, 1032, ["sorting", "onUpdate:sorting", "row-selection", "data", "columns", "page", "page-size", "page-size-options"])
                      ]),
                      _: 2
                    }, 1024))
                  : _createCommentVNode("", true)
              ], 64))
            }), 128))
          ]))
    ]),
    _: 1
  }))
}
}

})